import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import api from '../utils/api';
import ServiceTasksPanel from './ServiceTasksPanel';
import AdminMenuTab from './admin/AdminMenuTab';
import AdminLibraryTab from './admin/AdminLibraryTab';
import AdminInventoryTab from './admin/AdminInventoryTab';
import AdminPlanningTab from './admin/AdminPlanningTab';
import {
  AdminHeader,
  AdminTabNav,
  AdminSettingsTab,
  AdminStaffTab,
  AdminInsightsTab,
} from './admin/AdminPanelParts';
import { devLog } from '../utils/logger';
import {
  AlertTriangle,
  BookOpen,
  Calculator,
  Package,
  Settings2,
  TrendingUp,
  Users,
  UtensilsCrossed,
  X,
} from 'lucide-react';

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-xl border-t sm:border border-[#7D8B69]/20 max-h-[80vh] overflow-auto shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="bg-[#1B3A1A] text-white px-5 py-4 flex justify-between items-center sticky top-0 sm:rounded-t-xl">
          <h3 className="text-sm font-bold uppercase tracking-wider">{title}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/20 transition-colors"><X size={18} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState('menu');
  const [menu, setMenu] = useState([]);
  const [pins, setPins] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [report, setReport] = useState(null);
  const [kitchenState, setKitchenState] = useState('closed');
  const [library, setLibrary] = useState({ categories: {}, items: [] });
  const [allCategories, setAllCategories] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [openResult, setOpenResult] = useState(null);
  const [closeResult, setCloseResult] = useState(null);

  const [pinForm, setPinForm] = useState({ pin: '', role: 'order_taker', label: '' });
  const [libForm, setLibForm] = useState({ name: '', description: '', price: '', category: 'Mains', low_threshold: '5', recipe: [], has_double: false, double_price: '' });
  const [editingLibItem, setEditingLibItem] = useState(null);
  const [ingForm, setIngForm] = useState({ name: '', stock_qty: '', unit: 'pieces', low_threshold: '10', cost_per_unit: '', group: 'Other' });
  const [thresholds, setThresholds] = useState({ new_warning: 3, new_urgent: 5, new_critical: 8, pickup_warning: 1, pickup_urgent: 3, pickup_critical: 5 });
  const [newCat, setNewCat] = useState('');
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [recipeForm, setRecipeForm] = useState([]);
  const [collapsedCats, setCollapsedCats] = useState({});
  const [collapsedGroups, setCollapsedGroups] = useState({});

  // Events
  const [events, setEvents] = useState([]);
  const [eventForm, setEventForm] = useState({ name: '', library_item_ids: [] });
  const [editingEvent, setEditingEvent] = useState(null);
  const [notification, setNotification] = useState(null);
  const [servingsEdit, setServingsEdit] = useState({}); // {itemId: "value"}
  const [thresholdEdit, setThresholdEdit] = useState({});
  const [priceEdit, setPriceEdit] = useState({});

  const notify = (msg) => { setNotification(msg); setTimeout(() => setNotification(null), 3000); };

  // Planning state
  const [planItems, setPlanItems] = useState([]);
  const [planResult, setPlanResult] = useState(null);
  const [capacity, setCapacity] = useState([]);
  const [shoppingList, setShoppingList] = useState([]);
  // Communications
  const [comms, setComms] = useState({});
  // Analytics
  const [popular, setPopular] = useState(null);
  const [prepEstimate, setPrepEstimate] = useState(null);

  const [modal, setModal] = useState(null);
  const [modalData, setModalData] = useState({});

  const fetchMenu = useCallback(async () => { try { const { data } = await api.get('/menu'); setMenu(data); } catch (err) { devLog.error('Fetch failed:', err); } }, []);
  const fetchPins = useCallback(async () => { try { const { data } = await api.get('/admin/pins'); setPins(data); } catch (err) { devLog.error('Fetch failed:', err); } }, []);
  const fetchAnalytics = useCallback(async () => { try { const { data } = await api.get('/admin/analytics'); setAnalytics(data); } catch (err) { devLog.error('Fetch failed:', err); } }, []);
  const fetchMode = useCallback(async () => { try { const { data } = await api.get('/kitchen/state'); setKitchenState(data.state); } catch (err) { devLog.error('Fetch failed:', err); } }, []);
  const fetchLibrary = useCallback(async () => { try { const { data } = await api.get('/library'); setLibrary(data); } catch (err) { devLog.error('Fetch failed:', err); } }, []);
  const fetchCategories = useCallback(async () => { try { const { data } = await api.get('/library/categories'); setAllCategories(data.categories); } catch (err) { devLog.error('Fetch failed:', err); } }, []);
  const fetchIngredients = useCallback(async () => { try { const { data } = await api.get('/ingredients'); setIngredients(data); } catch (err) { devLog.error('Fetch failed:', err); } }, []);
  const fetchEvents = useCallback(async () => { try { const { data } = await api.get('/events'); setEvents(data); } catch (err) { devLog.error('Fetch failed:', err); } }, []);
  const fetchComms = useCallback(async () => { try { const { data } = await api.get('/settings/communications'); setComms(data); } catch (err) { devLog.error('Fetch failed:', err); } }, []);
  const fetchPopular = useCallback(async () => { try { const { data } = await api.get('/admin/analytics/popular'); setPopular(data); } catch (err) { devLog.error('Fetch failed:', err); } }, []);
  const fetchPrepEstimate = useCallback(async () => { try { const { data } = await api.get('/kitchen/prep-estimate'); setPrepEstimate(data); } catch (err) { devLog.error('Fetch failed:', err); } }, []);
  const fetchShoppingList = useCallback(async () => { try { const { data } = await api.get('/planning/shopping-list'); setShoppingList(data.items || []); } catch (err) { devLog.error('Shopping list fetch failed:', err); } }, []);

  useEffect(() => {
    fetchMenu(); fetchPins(); fetchAnalytics(); fetchMode(); fetchLibrary(); fetchCategories(); fetchIngredients(); fetchEvents(); fetchShoppingList();
    api.get('/settings/thresholds').then(r => setThresholds(r.data)).catch(() => {});
  }, [fetchMenu, fetchPins, fetchAnalytics, fetchMode, fetchLibrary, fetchCategories, fetchIngredients, fetchEvents, fetchShoppingList]);

  const onWsMessage = useCallback((msg) => {
    if (msg.type === 'menu_update' || msg.type === 'item_86') fetchMenu();
    if (['new_order', 'order_status', 'order_cancelled', 'daily_reset'].includes(msg.type)) fetchAnalytics();
    if (msg.type === 'kitchen_state') setKitchenState(msg.state);
    if (msg.type === 'settings_update' && msg.thresholds) setThresholds(msg.thresholds);
  }, [fetchMenu, fetchAnalytics]);
  useWebSocket(onWsMessage);

  // Kitchen
  const openKitchen = async () => { try { const { data } = await api.post('/kitchen/open'); setKitchenState('open'); setOpenResult(data); } catch (e) { alert(e.response?.data?.detail || 'Error'); } };
  const closeKitchen = async () => { try { const { data } = await api.post('/kitchen/close'); setKitchenState('closed'); setCloseResult(data); setModal('close-result'); } catch (e) { alert(e.response?.data?.detail || 'Error'); } };

  // Menu (read-only now, driven from library)
  const toggle86 = async (id) => { try { await api.put(`/menu/${id}/86`); } catch (err) { devLog.error('Fetch failed:', err); } };
  const uploadImage = (itemId) => { const i = document.createElement('input'); i.type = 'file'; i.accept = 'image/*'; i.onchange = async (e) => { const f = e.target.files[0]; if (!f) return; const fd = new FormData(); fd.append('file', f); try { await api.post(`/menu/${itemId}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); } catch (err) { devLog.error('Fetch failed:', err); } }; i.click(); };
  const deleteMenuItem = async (id) => { setModalData({ action: () => api.delete(`/menu/${id}`).then(fetchMenu), text: 'Remove from today\'s menu?' }); setModal('confirm'); };
  const updateServings = async (id, servings) => { try { await api.put(`/menu/${id}`, { servings_available: Math.max(0, servings) }); fetchMenu(); notify(`Servings updated to ${Math.max(0, servings)}`); } catch (err) { devLog.error('Fetch failed:', err); } };
  const saveServingsManual = async (id) => {
    const val = parseInt(servingsEdit[id]);
    if (isNaN(val) || val < 0) return;
    try { await api.put(`/menu/${id}`, { servings_available: val }); fetchMenu(); setServingsEdit(s => { const n = {...s}; delete n[id]; return n; }); notify(`Servings set to ${val}`); } catch (err) { devLog.error('Fetch failed:', err); }
  };
  const saveThresholdManual = async (id) => {
    const val = parseInt(thresholdEdit[id]);
    if (isNaN(val) || val < 0) return;
    try { await api.put(`/menu/${id}`, { low_threshold: val }); fetchMenu(); setThresholdEdit(s => { const n = {...s}; delete n[id]; return n; }); notify(`Low alert set to ${val}`); } catch (err) { devLog.error('Threshold save failed:', err); }
  };
  const savePriceManual = async (id) => {
    const val = parseFloat(priceEdit[id]);
    if (isNaN(val) || val < 0) return;
    try { await api.put(`/menu/${id}`, { price: val }); fetchMenu(); setPriceEdit(s => { const n = {...s}; delete n[id]; return n; }); notify(`Event price set to $${val.toFixed(2)}`); } catch (err) { alert(err.response?.data?.detail || 'Price save failed'); }
  };

  // Recipe on menu items
  const normalizeRecipeRow = (r = {}) => ({
    row_id: r.row_id || `recipe_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    ingredient_id: r.ingredient_id || '',
    qty_per_serving: r.qty_per_serving || 1,
    checkout_required: !!r.checkout_required,
    choice_set: r.choice_set || 'Protein',
    allow_extra: !!r.allow_extra,
    extra_price: r.extra_price || '',
  });
  const startEditRecipe = (item) => { setEditingRecipe(item.id); setRecipeForm((item.recipe || []).map(normalizeRecipeRow)); };
  const addRecipeRow = () => setRecipeForm(f => [...f, normalizeRecipeRow()]);
  const removeRecipeRow = (idx) => setRecipeForm(f => f.filter((_, i) => i !== idx));
  const updateRecipeRow = (idx, field, val) => setRecipeForm(f => f.map((r, i) => i === idx ? { ...r, [field]: val } : r));
  const cleanRecipeRows = (rows) => rows.filter(r => r.ingredient_id).map(r => ({
    ingredient_id: r.ingredient_id,
    qty_per_serving: parseFloat(r.qty_per_serving) || 0,
    checkout_required: !!r.checkout_required,
    choice_set: r.checkout_required ? (r.choice_set || 'Choice') : '',
    allow_extra: !!r.allow_extra,
    extra_price: r.allow_extra ? (parseFloat(r.extra_price) || 0) : 0,
    extra_qty_per_selection: 1,
  }));
  const saveMenuRecipe = async () => { const valid = cleanRecipeRows(recipeForm); try { await api.put(`/menu/${editingRecipe}/recipe`, valid); setEditingRecipe(null); fetchMenu(); notify('Recipe saved'); } catch (e) { alert(e.response?.data?.detail || 'Error'); } };

  // PINs
  const createPin = async () => { if (!pinForm.pin || !pinForm.label) return; try { await api.post('/admin/pins', pinForm); setPinForm({ pin: '', role: 'order_taker', label: '' }); fetchPins(); notify('PIN created'); } catch (e) { alert(e.response?.data?.detail || 'Error'); } };
  const deletePin = async (id) => { setModalData({ action: () => api.delete(`/admin/pins/${id}`).then(fetchPins), text: 'Delete this PIN?' }); setModal('confirm'); };
  const resetPin = async () => { const { pinId, newPin } = modalData; if (!newPin || newPin.length < 4) return; try { await api.put(`/admin/pins/${pinId}/reset`, { new_pin: newPin }); setModal(null); } catch (e) { alert(e.response?.data?.detail || 'Error'); } };
  const dailyReset = async () => { try { await api.post('/admin/daily-reset'); fetchAnalytics(); fetchMenu(); setModal(null); } catch (e) { alert(e.response?.data?.detail || 'Error'); } };
  const generateReport = async () => { try { const { data } = await api.get('/admin/report'); setReport(data); } catch (err) { devLog.error('Fetch failed:', err); } };
  const downloadCSV = async () => { try { const { data } = await api.get('/admin/report/csv', { responseType: 'blob' }); const url = window.URL.createObjectURL(new Blob([data])); const a = document.createElement('a'); a.href = url; a.download = `wildcraft-report-${new Date().toISOString().split('T')[0]}.csv`; a.click(); } catch (err) { devLog.error('Fetch failed:', err); } };

  // Library CRUD
  const resetLibForm = () => { setLibForm({ name: '', description: '', price: '', category: 'Mains', low_threshold: '5', recipe: [], has_double: false, double_price: '' }); setEditingLibItem(null); };
  const startEditLibItem = (item) => {
    setEditingLibItem(item.id);
    setLibForm({ name: item.name, description: item.description || '', price: item.price.toString(), category: item.category || 'Mains', low_threshold: (item.low_threshold || 5).toString(), recipe: (item.recipe || []).map(normalizeRecipeRow), has_double: item.has_double || false, double_price: item.double_price ? item.double_price.toString() : '' });
  };
  const saveLibItem = async () => {
    if (!libForm.name || !libForm.price) return;
    const payload = { name: libForm.name, description: libForm.description, price: parseFloat(libForm.price), category: libForm.category, low_threshold: parseInt(libForm.low_threshold) || 5, recipe: cleanRecipeRows(libForm.recipe), has_double: libForm.has_double, double_price: parseFloat(libForm.double_price) || 0 };
    try {
      if (editingLibItem) { await api.put(`/library/${editingLibItem}`, payload); notify('Library item updated'); }
      else { await api.post('/library', payload); notify('Added to library'); }
      resetLibForm(); fetchLibrary();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };
  const addToMenu = async (id) => { try { await api.post(`/library/${id}/to-menu`); fetchMenu(); fetchLibrary(); notify('Added to menu'); } catch (e) { alert(e.response?.data?.detail || 'Error'); } };
  const removeFromMenu = async (libId) => { try { await api.delete(`/menu/by-library/${libId}`); fetchMenu(); notify('Removed from menu'); } catch (e) { alert(e.response?.data?.detail || 'Error'); } };
  const deleteLibItem = async (id) => { setModalData({ action: () => api.delete(`/library/${id}`).then(fetchLibrary), text: 'Remove from library?' }); setModal('confirm'); };
  const addCategory = async () => { if (!newCat.trim()) return; try { await api.post('/library/categories', { name: newCat.trim() }); setNewCat(''); fetchCategories(); } catch (err) { devLog.error('Fetch failed:', err); } };
  const isOnMenu = (libId) => menu.some(m => m.library_item_id === libId);
  const toggleCat = (cat) => setCollapsedCats(c => ({ ...c, [cat]: !c[cat] }));

  // Events
  const saveEvent = async () => {
    if (!eventForm.name) return;
    try {
      if (editingEvent) { await api.put(`/events/${editingEvent}`, eventForm); notify('Event updated'); }
      else { await api.post('/events', eventForm); notify('Event created'); }
      setEventForm({ name: '', library_item_ids: [] }); setEditingEvent(null); fetchEvents();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };
  const loadEvent = async (id) => {
    try { const { data } = await api.post(`/events/${id}/load`); fetchMenu(); fetchLibrary(); setNotification(`Loaded "${data.event_name}" — ${data.added.length} items added, replaced ${data.replaced} previous items`); setTimeout(() => setNotification(null), 5000); } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };
  const deleteEvent = async (id) => { setModalData({ action: () => api.delete(`/events/${id}`).then(fetchEvents), text: 'Delete this event?' }); setModal('confirm'); };
  const toggleEventItem = (libId) => {
    setEventForm(f => {
      const ids = f.library_item_ids.includes(libId) ? f.library_item_ids.filter(i => i !== libId) : [...f.library_item_ids, libId];
      return { ...f, library_item_ids: ids };
    });
  };

  // Library recipe form helpers
  const addLibRecipeRow = () => setLibForm(f => ({ ...f, recipe: [...f.recipe, normalizeRecipeRow()] }));
  const removeLibRecipeRow = (idx) => setLibForm(f => ({ ...f, recipe: f.recipe.filter((_, i) => i !== idx) }));
  const updateLibRecipeRow = (idx, field, val) => setLibForm(f => ({ ...f, recipe: f.recipe.map((r, i) => i === idx ? { ...r, [field]: val } : r) }));

  // Ingredients
  const addIngredient = async () => {
    if (!ingForm.name) return;
    try { await api.post('/ingredients', { ...ingForm, stock_qty: parseFloat(ingForm.stock_qty) || 0, low_threshold: parseFloat(ingForm.low_threshold) || 10, cost_per_unit: parseFloat(ingForm.cost_per_unit) || 0 });
      setIngForm({ name: '', stock_qty: '', unit: 'pieces', low_threshold: '10', cost_per_unit: '', group: 'Other' }); fetchIngredients(); notify('Ingredient added');
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };
  const deleteIngredient = async (id) => { setModalData({ action: () => api.delete(`/ingredients/${id}`).then(fetchIngredients), text: 'Delete ingredient?' }); setModal('confirm'); };
  const updateIngStock = async (id, qty) => { try { await api.put(`/ingredients/${id}`, { stock_qty: qty }); fetchIngredients(); } catch (err) { devLog.error('Fetch failed:', err); } };
  const toggleGroup = (grp) => setCollapsedGroups(g => ({ ...g, [grp]: !g[grp] }));
  const ingGroups = ['Produce', 'Beef', 'Pork', 'Chicken', 'Seafood', 'Dairy', 'Breads', 'Desserts', 'Spices', 'Other'];
  const groupedIngs = ingGroups.map(g => ({ group: g, items: ingredients.filter(i => (i.group || 'Other') === g) })).filter(g => g.items.length > 0);
  // Planning
  const fetchCapacity = async () => { try { const { data } = await api.get('/inventory/capacity'); setCapacity(data); } catch (err) { devLog.error('Fetch failed:', err); } };
  const addPlanRow = () => setPlanItems(p => [...p, { row_id: `plan_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, library_item_id: '', desired_servings: 0 }]);
  const updatePlanRow = (idx, field, val) => setPlanItems(p => p.map((r, i) => i === idx ? { ...r, [field]: val } : r));
  const removePlanRow = (idx) => setPlanItems(p => p.filter((_, i) => i !== idx));
  const persistShoppingList = async (items) => {
    const { data } = await api.put('/planning/shopping-list', { items });
    setShoppingList(data.items || []);
    return data.items || [];
  };
  const buildPlanningShortages = (summary = []) => summary
    .filter(s => s.to_buy > 0)
    .map(s => ({
      id: `plan_${s.ingredient_id || s.ingredient_name}`,
      name: s.ingredient_name,
      qty: s.to_buy,
      unit: s.unit,
      checked: false,
      source: 'planning',
      ingredient_id: s.ingredient_id || '',
    }));
  const syncPlanShortagesToShoppingList = async (summary = []) => {
    const nonPlanningItems = shoppingList.filter(item => item.source !== 'planning');
    const shortages = buildPlanningShortages(summary);
    await persistShoppingList([...nonPlanningItems, ...shortages]);
    if (shortages.length > 0) notify(`${shortages.length} shortage${shortages.length === 1 ? '' : 's'} added to shopping list`);
    else notify('No ingredient shortages in this plan');
  };
  const runPlan = async () => {
    const valid = planItems.filter(p => p.library_item_id && p.desired_servings > 0);
    if (!valid.length) return;
    try {
      const { data } = await api.post('/inventory/plan', { plans: valid });
      setPlanResult(data);
      await syncPlanShortagesToShoppingList(data.shopping_summary || []);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };
  const addShoppingItem = async (item) => {
    const next = [...shoppingList, { id: `shop_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, checked: false, source: 'manual', ingredient_id: '', ...item }];
    try { await persistShoppingList(next); notify('Shopping list updated'); } catch (err) { devLog.error('Shopping add failed:', err); }
  };
  const updateShoppingItem = async (id, updates) => {
    try { await persistShoppingList(shoppingList.map(item => item.id === id ? { ...item, ...updates } : item)); } catch (err) { devLog.error('Shopping update failed:', err); }
  };
  const removeShoppingItem = async (id) => {
    try { await persistShoppingList(shoppingList.filter(item => item.id !== id)); } catch (err) { devLog.error('Shopping remove failed:', err); }
  };
  const clearShoppingList = async () => {
    try { const { data } = await api.delete('/planning/shopping-list'); setShoppingList(data.items || []); notify('Shopping list cleared'); } catch (err) { devLog.error('Shopping clear failed:', err); }
  };
  const downloadAuthenticatedFile = async (endpoint, filename, mimeType) => {
    try {
      const { data } = await api.get(endpoint, { responseType: 'blob' });
      const blob = data instanceof Blob ? data : new Blob([data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      devLog.error('Download failed:', err);
      alert(err.response?.data?.detail || 'Download failed. Please log in again and retry.');
    }
  };
  const downloadPlanningCSV = () => downloadAuthenticatedFile('/planning/shopping-list/csv', `planning-shopping-list-${new Date().toISOString().split('T')[0]}.csv`, 'text/csv');
  const downloadPlanningPDF = () => downloadAuthenticatedFile('/planning/shopping-list/pdf', `planning-shopping-list-${new Date().toISOString().split('T')[0]}.pdf`, 'application/pdf');

  const tabs = [
    { id: 'menu', label: 'Menu', icon: UtensilsCrossed },
    { id: 'library', label: 'Library', icon: BookOpen },
    { id: 'inventory', label: 'Inventory', icon: Package },
    { id: 'planning', label: 'Planning', icon: Calculator },
    { id: 'settings', label: 'Settings', icon: Settings2 },
    { id: 'staff', label: 'Staff', icon: Users },
    { id: 'insights', label: 'Data', icon: TrendingUp },
  ];

  const isSuperAdmin = ['super_admin', 'admin'].includes(user.role);

  const handleTabSelect = (nextTab) => {
    setTab(nextTab);
    if (nextTab === 'insights') { fetchAnalytics(); generateReport(); fetchPopular(); fetchPrepEstimate(); }
    if (nextTab === 'library') { fetchLibrary(); fetchCategories(); }
    if (nextTab === 'inventory') { fetchIngredients(); }
    if (nextTab === 'planning') { fetchIngredients(); fetchLibrary(); fetchCapacity(); fetchShoppingList(); }
    if (nextTab === 'settings') { fetchComms(); }
    if (nextTab === 'menu') { fetchMenu(); fetchPrepEstimate(); }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col" style={{fontFamily: "'Manrope', sans-serif"}} data-testid="admin-panel">
      <AdminHeader isSuperAdmin={isSuperAdmin} user={user} kitchenState={kitchenState} openKitchen={openKitchen} setModal={setModal} setModalData={setModalData} dailyReset={dailyReset} logout={logout} />

      {kitchenState === 'open' && <div className="bg-[#4A7C3F] text-white px-6 py-2 text-xs font-bold uppercase tracking-wider text-center">KITCHEN OPEN</div>}

      {notification && <div className="bg-[#4A7C3F] text-white px-6 py-2.5 text-sm font-semibold text-center animate-slide-in" data-testid="admin-notification">{notification}</div>}

      {openResult && (
        <Modal open={true} onClose={() => setOpenResult(null)} title="Kitchen Opening - Prep Checklist">
          <div className="space-y-3">
            {openResult.prep_list?.filter(p => p.servings > 0).map(p => (
              <div key={`prep-item-${p.item}-${p.servings}`} className="border border-[#7D8B69]/15 p-2">
                <div className="font-heading font-bold">{p.item} <span className="font-mono text-xs text-[#7D8B69]">({p.servings} servings)</span></div>
                {p.ingredients.length > 0 ? p.ingredients.map((i, idx) => (
                  <div key={`prep-ingredient-${i.name}-${idx}`} className={`font-mono text-xs mt-0.5 ${i.sufficient ? 'text-[#4A7C3F]' : 'text-[#FF3333] font-bold'}`}>
                    {i.name}: need {i.need} {i.unit} (have {i.have}) {!i.sufficient && '- LOW!'}
                  </div>
                )) : <div className="font-mono text-xs text-[#7D8B69] italic">No recipe set</div>}
              </div>
            ))}
            {openResult.opening_tasks?.length > 0 && (
              <div className="border border-[#7D8B69]/20 rounded-lg p-3">
                <h4 className="font-mono text-xs font-bold uppercase text-[#7D8B69] mb-2">Opening Checklist</h4>
                {openResult.opening_tasks.map((task, i) => <label key={`opening-${task}`} className="flex items-center gap-2 font-mono text-sm py-1"><input type="checkbox" data-testid={`opening-check-${i}`} />{task}</label>)}
              </div>
            )}
            {openResult.low_ingredients?.length > 0 && (
              <div className="border-2 border-[#FF3333] p-2">
                <div className="font-mono text-xs font-bold text-[#FF3333] uppercase mb-1"><AlertTriangle size={12} className="inline" /> Low Ingredients</div>
                {openResult.low_ingredients.map(i => <div key={i.id} className="font-mono text-xs">{i.name}: {i.stock_qty} {i.unit}</div>)}
              </div>
            )}
            <button onClick={() => setOpenResult(null)} className="w-full h-12 bg-[#1B3A1A] hover:bg-[#2C4C2B] text-white rounded-lg font-semibold uppercase tracking-wider">GOT IT - START SERVICE</button>
          </div>
        </Modal>
      )}

      <AdminTabNav tabs={tabs} tab={tab} onTabSelect={handleTabSelect} />

      <div className="flex-1 overflow-auto p-5 sm:p-6">
        {/* ===== MENU TAB (Read-only: items from Library) ===== */}
        {tab === 'menu' && (
          <AdminMenuTab
            menu={menu}
            setTab={setTab}
            kitchenState={kitchenState}
            priceEdit={priceEdit}
            setPriceEdit={setPriceEdit}
            savePriceManual={savePriceManual}
            uploadImage={uploadImage}
            updateServings={updateServings}
            servingsEdit={servingsEdit}
            setServingsEdit={setServingsEdit}
            saveServingsManual={saveServingsManual}
            thresholdEdit={thresholdEdit}
            setThresholdEdit={setThresholdEdit}
            saveThresholdManual={saveThresholdManual}
            editingRecipe={editingRecipe}
            setEditingRecipe={setEditingRecipe}
            startEditRecipe={startEditRecipe}
            toggle86={toggle86}
            deleteMenuItem={deleteMenuItem}
            recipeForm={recipeForm}
            ingredients={ingredients}
            updateRecipeRow={updateRecipeRow}
            removeRecipeRow={removeRecipeRow}
            addRecipeRow={addRecipeRow}
            saveMenuRecipe={saveMenuRecipe}
          />
        )}

        {/* ===== LIBRARY TAB ===== */}
        {tab === 'library' && (
          <AdminLibraryTab
            events={events}
            collapsedCats={collapsedCats}
            setCollapsedCats={setCollapsedCats}
            eventForm={eventForm}
            setEventForm={setEventForm}
            saveEvent={saveEvent}
            editingEvent={editingEvent}
            setEditingEvent={setEditingEvent}
            library={library}
            toggleEventItem={toggleEventItem}
            loadEvent={loadEvent}
            deleteEvent={deleteEvent}
            libForm={libForm}
            setLibForm={setLibForm}
            editingLibItem={editingLibItem}
            allCategories={allCategories}
            addLibRecipeRow={addLibRecipeRow}
            updateLibRecipeRow={updateLibRecipeRow}
            removeLibRecipeRow={removeLibRecipeRow}
            ingredients={ingredients}
            saveLibItem={saveLibItem}
            resetLibForm={resetLibForm}
            newCat={newCat}
            setNewCat={setNewCat}
            addCategory={addCategory}
            toggleCat={toggleCat}
            isOnMenu={isOnMenu}
            startEditLibItem={startEditLibItem}
            addToMenu={addToMenu}
            removeFromMenu={removeFromMenu}
            deleteLibItem={deleteLibItem}
          />
        )}

        {/* ===== INVENTORY TAB ===== */}
        {tab === 'inventory' && (
          <AdminInventoryTab
            ingForm={ingForm}
            setIngForm={setIngForm}
            ingGroups={ingGroups}
            addIngredient={addIngredient}
            ingredients={ingredients}
            groupedIngs={groupedIngs}
            collapsedGroups={collapsedGroups}
            toggleGroup={toggleGroup}
            updateIngStock={updateIngStock}
            deleteIngredient={deleteIngredient}
          />
        )}

        {/* ===== PLANNING TAB ===== */}
        {tab === 'planning' && (
          <AdminPlanningTab
            capacity={capacity}
            planItems={planItems}
            library={library}
            ingredients={ingredients}
            updatePlanRow={updatePlanRow}
            removePlanRow={removePlanRow}
            addPlanRow={addPlanRow}
            runPlan={runPlan}
            planResult={planResult}
            shoppingList={shoppingList}
            addShoppingItem={addShoppingItem}
            updateShoppingItem={updateShoppingItem}
            removeShoppingItem={removeShoppingItem}
            clearShoppingList={clearShoppingList}
            downloadPlanningCSV={downloadPlanningCSV}
            downloadPlanningPDF={downloadPlanningPDF}
          />
        )}

        {/* ===== SETTINGS TAB ===== */}
        {tab === 'settings' && <AdminSettingsTab comms={comms} setComms={setComms} api={api} notify={notify} thresholds={thresholds} setThresholds={setThresholds} ServiceTasksPanelComponent={ServiceTasksPanel} />}

        {/* ===== STAFF TAB ===== */}
        {tab === 'staff' && <AdminStaffTab pinForm={pinForm} setPinForm={setPinForm} createPin={createPin} pins={pins} setModalData={setModalData} setModal={setModal} deletePin={deletePin} />}

        {/* ===== INSIGHTS TAB ===== */}
        {tab === 'insights' && <AdminInsightsTab analytics={analytics} generateReport={generateReport} downloadCSV={downloadCSV} prepEstimate={prepEstimate} popular={popular} report={report} />}
      </div>

      {/* MODALS */}
      <Modal open={modal === 'reset-pin'} onClose={() => setModal(null)} title={`Reset PIN — ${modalData.pinLabel || ''}`}>
        <div className="space-y-3">
          <input type="password" value={modalData.newPin || ''} onChange={e => setModalData(d => ({ ...d, newPin: e.target.value }))} placeholder="New PIN (4+ digits)" className="w-full border border-[#7D8B69]/30 rounded-lg px-3 py-2.5 text-sm bg-[#FDFBF7]" autoFocus />
          {modalData.newPin && modalData.newPin.length < 4 && <p className="font-mono text-xs text-[#FF3333]">PIN must be at least 4 digits</p>}
          <button onClick={resetPin} disabled={!modalData.newPin || modalData.newPin.length < 4} className="w-full h-12 bg-[#1B3A1A] hover:bg-[#2C4C2B] text-white rounded-lg font-semibold uppercase tracking-wider disabled:opacity-40">RESET PIN</button>
        </div>
      </Modal>

      <Modal open={modal === 'confirm'} onClose={() => setModal(null)} title="Confirm">
        <div className="space-y-3">
          <p className="font-mono text-sm">{modalData.text}</p>
          <div className="flex gap-2">
            <button onClick={() => setModal(null)} className="flex-1 h-12 border border-[#7D8B69]/20 rounded-lg font-mono font-bold uppercase">CANCEL</button>
            <button onClick={async () => { if (modalData.action) await modalData.action(); setModal(null); }} className="flex-1 h-12 bg-[#FF3333] text-white font-mono font-bold uppercase">CONFIRM</button>
          </div>
        </div>
      </Modal>

      <Modal open={modal === 'confirm-close'} onClose={() => setModal(null)} title="Close Kitchen?">
        <div className="space-y-3">
          <p className="font-mono text-sm">This will lock the kitchen and generate a wrap-up report.</p>
          <div className="flex gap-2">
            <button onClick={() => setModal(null)} className="flex-1 h-12 border border-[#7D8B69]/20 rounded-lg font-mono font-bold uppercase">CANCEL</button>
            <button onClick={() => { closeKitchen(); setModal(null); }} className="flex-1 h-12 bg-[#FF3333] text-white font-mono font-bold uppercase">CLOSE KITCHEN</button>
          </div>
        </div>
      </Modal>

      <Modal open={modal === 'close-result'} onClose={() => setModal(null)} title="Kitchen Closed - Wrap Up">
        {closeResult && (
          <div className="space-y-3">
            <div className="font-mono text-sm space-y-1">
              <div className="font-bold text-[#1B3A1A]">Today: {closeResult.summary.total_orders} orders, ${closeResult.summary.revenue.toFixed(2)} revenue</div>
              {closeResult.active_orders_remaining > 0 && <div className="text-[#FF3333] font-bold">{closeResult.active_orders_remaining} orders still active!</div>}
            </div>
            <div className="border border-[#7D8B69]/20 rounded-lg p-3">
              <h4 className="font-mono text-xs font-bold uppercase text-[#7D8B69] mb-2">Wrap-Up Checklist</h4>
              {closeResult.wrap_up.map((task, i) => <label key={`closing-${task}`} className="flex items-center gap-2 font-mono text-sm py-1"><input type="checkbox" data-testid={`closing-check-${i}`} />{task}</label>)}
            </div>
            <div className="border border-[#7D8B69]/20 rounded-lg p-3">
              <h4 className="font-mono text-xs font-bold uppercase text-[#7D8B69] mb-2">Stock Remaining</h4>
              {closeResult.stock_status.map((s, idx) => <div key={`close-stock-${s.name}-${idx}`} className={`font-mono text-xs py-0.5 ${s.is_86 || s.remaining <= 0 ? 'text-[#FF3333]' : ''}`}>{s.name}: {s.is_86 ? "86'd" : `${s.remaining} left`}</div>)}
            </div>
            <button onClick={() => setModal(null)} className="w-full h-12 bg-[#1B3A1A] hover:bg-[#2C4C2B] text-white rounded-lg font-semibold uppercase tracking-wider">DONE</button>
          </div>
        )}
      </Modal>
    </div>
  );
}

